/**
 * Pre-Pack hub API client.
 *
 * Mirrors the response shapes of `src/api/prepack-routes.ts`. Labor types are
 * intentionally identical to the Shipping hub so the same UX / wording works
 * across both pages — only the URL prefix differs (`/api/prepack/labor/*`).
 */

import { apiFetch } from "@/lib/api";
import type { InventoryBox } from "@/lib/inventoryBoxApi";

// ---------------------------------------------------------------------------
// Carton queue
// ---------------------------------------------------------------------------

export interface CartonItem {
  sku: string;
  source: "serialized" | "unserialized";
  plannedQty: number;
  packedQty: number;
  pending: number;
  isSerialized: boolean;
  /** Present on queue/carton API — planner volume fraction per discrete unit (serialization detail). */
  volumePerUnit?: number;
  /** When set, **`plannedQty` / `packedQty` / `pending`** are kit-equivalents (**fractional = incomplete kit**, never split across cartons). */
  kitDivisor?: number;
  plannedPhysicalSerialQty?: number;
  packedPhysicalSerialQty?: number;
  pendingPhysicalSerialQty?: number;
}

export interface CartonRow {
  shipmentId: number;
  cartonNumber: number;
  rowKey: string;
  batchId: number;
  mfrShipByDate: string | null;
  mfrRemark: string | null;
  soInstructions: string | null;
  lineItemInstructions: string | null;
  items: CartonItem[];
  isFullyPacked: boolean;
  /** Planner: unused volume in this carton (1 = empty). */
  remainingVolume: number;
  /** TRUE when the operator sealed this carton (no further packing). */
  sealed?: boolean;
  /** ISO timestamp when sealed; omitted when not sealed. */
  sealedAt?: string | null;
}

export async function fetchPrePackQueue(showFullyPacked: boolean): Promise<PrePackQueueResponse> {
  return apiFetch<PrePackQueueResponse>("/api/prepack/queue", {
    query: { showFullyPacked: showFullyPacked ? "true" : "false" },
    cache: "no-store",
  });
}

export interface PrePackQueueResponse {
  cartons: CartonRow[];
  transfers: TransferQueueRow[];
}

export interface TransferQueueRow {
  kind: "transfer";
  rowKey: string;
  transferOrderId: number;
  transferNumber: string | null;
  originLocationId: number;
  destinationLocationId: number;
  shipByDate: string | null;
  originLocationAbbreviation: string | null;
  destinationLocationAbbreviation: string | null;
  totalPackTarget: number;
  totalAllocated: number;
  isFullyPacked: boolean;
  pendingPackQtyRequestCount: number;
}

export interface TransferPackLine {
  id: number;
  line_number: number | null;
  sku_id: string | null;
  quantity: number;
  approved_pack_quantity: number | null;
  has_pending_pack_qty_request: boolean;
  pack_target: number;
  allocated_quantity: number;
  unallocated_quantity: number;
  origin_on_hand: number | null;
}

export interface TransferPackInventoryContainer {
  hex: string;
  short_code: string;
  quantity: number;
}

export interface TransferPackAllocation {
  id: string;
  transfer_order_item_id: number;
  sku_id: string | null;
  pack_list_id: number;
  quantity: number;
  inventory_containers?: TransferPackInventoryContainer[];
}

export interface TransferPackBox {
  id: number;
  box_number: number;
  sealed: boolean;
  sealed_at: string | null;
  label_printed_at: string | null;
  allocations: TransferPackAllocation[];
}

export interface TransferPackWorkspace {
  id: number;
  transfer_number: string | null;
  status: string;
  origin_location_id: number;
  destination_location_id: number;
  origin_location_abbreviation: string | null;
  destination_location_abbreviation: string | null;
  origin_location_mrp_site_code: string | null;
  destination_location_mrp_site_code: string | null;
  ship_by_date: string | null;
  notes: string | null;
  lines: TransferPackLine[];
  boxes: TransferPackBox[];
  open_box: TransferPackBox | null;
  pending_pack_qty_request_count: number;
  is_fully_packed: boolean;
  all_boxes_sealed: boolean;
}

export interface TransferCartonPrintData {
  transfer_order_id: number;
  transfer_number: string | null;
  origin_location_id: number;
  destination_location_id: number;
  origin_location_abbreviation: string | null;
  destination_location_abbreviation: string | null;
  origin_location_mrp_site_code: string | null;
  destination_location_mrp_site_code: string | null;
  ship_by_date: string | null;
  box_id: number;
  box_number: number;
  lines: { sku_id: string | null; quantity: number }[];
}

export async function fetchPrePackTransfer(transferOrderId: number): Promise<TransferPackWorkspace> {
  const data = await apiFetch<{ transfer: TransferPackWorkspace }>(
    `/api/prepack/transfers/${String(transferOrderId)}`,
    { cache: "no-store" },
  );
  return data.transfer;
}

export async function fetchPrePackTransferCartonPrint(
  transferOrderId: number,
  boxNumber: number,
): Promise<TransferCartonPrintData> {
  const data = await apiFetch<{ print: TransferCartonPrintData }>(
    `/api/prepack/transfers/${String(transferOrderId)}/boxes/${String(boxNumber)}/print`,
    { cache: "no-store" },
  );
  return data.print;
}

export async function printPrePackTransferCartonLabel(
  transferOrderId: number,
  boxNumber: number,
  printer: string,
): Promise<{ printed: true; lineCount: number; labelCount: number }> {
  return apiFetch(`/api/prepack/transfers/${String(transferOrderId)}/boxes/${String(boxNumber)}/print`, {
    method: "POST",
    body: { printer },
  });
}

export async function addPrePackTransferAllocation(
  transferOrderId: number,
  body: { sku_id: string; quantity: number; inventory_box_hexes?: string[] },
): Promise<{
  allocation: TransferPackAllocation;
  packed_inventory_containers?: TransferPackInventoryContainer[];
  box: { id: number; box_number: number };
  packing_quantity_request: {
    id: string;
    requested_quantity: number;
  } | null;
  inventory_discrepancy_request: {
    id: string;
    quantity: number;
  } | null;
}> {
  return apiFetch(`/api/prepack/transfers/${String(transferOrderId)}/allocations`, {
    method: "POST",
    body,
  });
}

export async function deletePrePackTransferAllocation(
  transferOrderId: number,
  allocationId: string,
): Promise<{ deleted: true; released_inventory_containers?: TransferPackInventoryContainer[] }> {
  return apiFetch(`/api/prepack/transfers/${String(transferOrderId)}/allocations/${allocationId}`, {
    method: "DELETE",
  });
}

export async function preparePrePackTransferInventoryContainer(
  transferOrderId: number,
  hex: string,
): Promise<{ box: InventoryBox; restored: boolean }> {
  return apiFetch(
    `/api/prepack/transfers/${String(transferOrderId)}/inventory-containers/${encodeURIComponent(hex)}/prepare`,
    { method: "POST" },
  );
}

export async function updatePrePackTransferAllocation(
  transferOrderId: number,
  allocationId: string,
  body: { quantity: number },
): Promise<{
  allocation: TransferPackAllocation;
  packing_quantity_request: {
    id: string;
    requested_quantity: number;
  } | null;
  inventory_discrepancy_request: {
    id: string;
    quantity: number;
  } | null;
}> {
  return apiFetch(`/api/prepack/transfers/${String(transferOrderId)}/allocations/${allocationId}`, {
    method: "PATCH",
    body,
  });
}

export async function sealPrePackTransferBox(
  transferOrderId: number,
  boxId: number,
): Promise<{ box: { id: number; box_number: number; sealed: true }; auto_advanced: boolean }> {
  return apiFetch(`/api/prepack/transfers/${String(transferOrderId)}/boxes/${String(boxId)}/seal`, {
    method: "POST",
    body: {},
  });
}

export async function unsealPrePackTransferBox(
  transferOrderId: number,
  boxId: number,
): Promise<{
  box: { id: number; box_number: number; sealed: false };
  status_reverted: boolean;
  unsealed: true;
}> {
  return apiFetch(`/api/prepack/transfers/${String(transferOrderId)}/boxes/${String(boxId)}/unseal`, {
    method: "POST",
    body: {},
  });
}

export async function startNextPrePackTransferBox(
  transferOrderId: number,
): Promise<{ box: { id: number; box_number: number } }> {
  return apiFetch(`/api/prepack/transfers/${String(transferOrderId)}/boxes`, {
    method: "POST",
    body: {},
  });
}

export async function markPrePackTransferCartonLabelPrinted(
  transferOrderId: number,
  boxId: number,
): Promise<{ ok: true }> {
  return apiFetch(`/api/prepack/transfers/${String(transferOrderId)}/boxes/${String(boxId)}/mark-label-printed`, {
    method: "POST",
    body: {},
  });
}

export async function submitPrePackTransferShipLessRequest(
  transferOrderId: number,
  body: { transfer_order_item_id: number; reason?: string | null },
): Promise<{
  request: { id: string; requested_quantity: number; transfer_order_item_id: number };
}> {
  return apiFetch(`/api/prepack/transfers/${String(transferOrderId)}/ship-less-requests`, {
    method: "POST",
    body,
  });
}

export async function fetchPrePackCarton(shipmentId: number, cartonNumber: number): Promise<CartonRow> {
  const data = await apiFetch<{ carton: CartonRow }>(
    `/api/prepack/shipments/${shipmentId}/cartons/${cartonNumber}`,
    { cache: "no-store" },
  );
  return data.carton;
}

export interface PackOutComponent {
  variable_name: string | null;
  component_sku: string;
  category: string | null;
  description: string | null;
  variant: string | null;
  quantity_per_unit: number;
  quantity_total: number;
}

export interface PackOutSkuGroup {
  finished_sku: string;
  planned_units: number;
  kit_divisor: number | null;
  template_id: number | null;
  revision: number | null;
  revision_source: "approved" | "max" | null;
  has_template: boolean;
  components: PackOutComponent[];
}

export interface PackOutComponentsResponse {
  station: { id: number; name: string };
  groups: PackOutSkuGroup[];
}

export async function fetchPrePackCartonPackOutComponents(
  shipmentId: number,
  cartonNumber: number,
): Promise<PackOutComponentsResponse> {
  return apiFetch<PackOutComponentsResponse>(
    `/api/prepack/shipments/${shipmentId}/cartons/${cartonNumber}/pack-out-components`,
    { cache: "no-store" },
  );
}

// ---------------------------------------------------------------------------
// Open carton — serials and unserialized lines actually saved in DB
// ---------------------------------------------------------------------------

export interface CartonSerialRow {
  id: number;
  finished_sku: string | null;
  device_label_serial: string | null;
  box_serial: string | null;
  build_order_id: number | null;
  mv_reference_number: string | null;
  mv_id: number | null;
  /** Set when the operator used print mode (hub box label) instead of scanning a physical box label. */
  label_printed_serial_id: number | null;
}

export async function fetchCartonSerials(shipmentId: number, cartonNumber: number): Promise<CartonSerialRow[]> {
  const data = await apiFetch<{ serials: CartonSerialRow[] }>(
    `/api/prepack/shipments/${shipmentId}/cartons/${cartonNumber}/serials`,
    { cache: "no-store" },
  );
  return data.serials;
}

export interface CartonUnserializedItem {
  batchItemId: number;
  sku: string;
  shipmentQuantity: number;
  quantityPacked: number;
  plannedQty: number;
  pending: number;
  divisor: number;
  cartonNumber: number;
  isPinned: boolean;
}

export async function fetchCartonUnserialized(
  shipmentId: number,
  cartonNumber: number,
): Promise<CartonUnserializedItem[]> {
  const data = await apiFetch<{ items: CartonUnserializedItem[] }>(
    `/api/prepack/shipments/${shipmentId}/cartons/${cartonNumber}/unserialized`,
    { cache: "no-store" },
  );
  return data.items;
}

export interface SealDestinationCarton {
  cartonNumber: number;
  reason: "open_existing" | "new_carton";
  movedLines: Array<{
    sku: string;
    source: "serialized" | "unserialized";
    quantityMoved: number;
  }>;
}

export interface SealCartonPreviewResponse {
  shipmentId: number;
  sealedCartonNumber: number;
  primaryDestinationCartonNumber: number;
  destinationCartons: SealDestinationCarton[];
}

export async function fetchSealCartonPreview(
  shipmentId: number,
  cartonNumber: number,
): Promise<SealCartonPreviewResponse> {
  return apiFetch<SealCartonPreviewResponse>(
    `/api/prepack/shipments/${shipmentId}/cartons/${cartonNumber}/seal-preview`,
    { cache: "no-store" },
  );
}

export interface SealCartonResult {
  shipmentId: number;
  sealedCartonNumber: number;
  primaryDestinationCartonNumber: number;
  destinationCartons: SealDestinationCarton[];
}

export async function sealPrepackCarton(shipmentId: number, cartonNumber: number): Promise<SealCartonResult> {
  return apiFetch<SealCartonResult>(`/api/prepack/shipments/${shipmentId}/cartons/${cartonNumber}/seal`, {
    method: "POST",
  });
}

export async function unsealPrepackCarton(
  shipmentId: number,
  cartonNumber: number,
): Promise<{ shipmentId: number; cartonNumber: number; unsealed: true }> {
  return apiFetch(`/api/prepack/shipments/${shipmentId}/cartons/${cartonNumber}/unseal`, {
    method: "POST",
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export type PrePackBoxLabelMode = "scan" | "print";

export interface SaveBoxLabelInput {
  shipmentId: number;
  cartonNumber: number;
  deviceLabelSerial: string;
  /** Required when mode is "scan"; ignored when mode is "print". */
  boxScan?: string;
  mode?: PrePackBoxLabelMode;
  /** Used when mode is "print" and no station is selected. */
  printer?: string;
  /** Selected floor station. The server picks the queue that matches this box label's stock. */
  stationId?: number;
  /** Open hub labor session on this carton; ties the pairing to Time tracking and work-order cost. */
  laborSessionId?: number;
}

export interface SaveBoxLabelResult {
  serialFciId: number;
  cartonNumber: number;
  boxSerial?: string;
  printed?: boolean;
  /** Present when the pairing was saved but ZPL print failed. Recover with Reprint. */
  printWarning?: string;
  detail?: string;
}

export async function saveBoxLabel(input: SaveBoxLabelInput): Promise<SaveBoxLabelResult> {
  const mode = input.mode ?? "scan";
  return apiFetch<SaveBoxLabelResult>(`/api/prepack/shipments/${input.shipmentId}/box-label`, {
    method: "POST",
    body: {
      deviceLabelSerial: input.deviceLabelSerial,
      cartonNumber: input.cartonNumber,
      mode,
      ...(mode === "scan"
        ? { boxScan: input.boxScan ?? "" }
        : {
            ...(input.printer != null && input.printer !== "" ? { printer: input.printer } : {}),
            ...(input.stationId != null && input.stationId > 0 ? { stationId: input.stationId } : {}),
          }),
      ...(input.laborSessionId != null && input.laborSessionId > 0
        ? { laborSessionId: input.laborSessionId }
        : {}),
    },
  });
}

export async function removeBoxLabel(serialFciId: number): Promise<void> {
  await apiFetch(`/api/prepack/serial-fci/${serialFciId}/box-label`, { method: "DELETE" });
}

export async function reprintBoxLabel(
  serialFciId: number,
  input: { stationId?: number; printer?: string },
): Promise<{ printed: true }> {
  return apiFetch<{ printed: true }>(`/api/prepack/serial-fci/${serialFciId}/box-label/print`, {
    method: "POST",
    body: {
      ...(input.printer != null && input.printer !== "" ? { printer: input.printer } : {}),
      ...(input.stationId != null && input.stationId > 0 ? { stationId: input.stationId } : {}),
    },
  });
}

export interface PackUnserializedInput {
  shipmentId: number;
  cartonNumber: number;
  batchItemId: number;
  quantityPacked: number;
}

export async function packUnserialized(input: PackUnserializedInput): Promise<void> {
  await apiFetch(
    `/api/prepack/shipments/${input.shipmentId}/cartons/${input.cartonNumber}/unserialized`,
    {
      method: "POST",
      body: {
        batchItemId: input.batchItemId,
        quantityPacked: input.quantityPacked,
      },
    },
  );
}

export async function unpackUnserialized(
  shipmentId: number,
  cartonNumber: number,
  batchItemId: number,
): Promise<void> {
  await apiFetch(
    `/api/prepack/shipments/${shipmentId}/cartons/${cartonNumber}/unserialized/${batchItemId}`,
    { method: "DELETE" },
  );
}

// ---------------------------------------------------------------------------
// Carton label printing
// ---------------------------------------------------------------------------

export interface CartonZpl {
  zpl: string;
  lineCount: number;
}

export async function fetchCartonZpl(shipmentId: number, cartonNumber: number): Promise<CartonZpl> {
  return apiFetch<CartonZpl>(`/api/prepack/shipments/${shipmentId}/cartons/${cartonNumber}/zpl`, {
    cache: "no-store",
  });
}

export async function printCartonLabel(
  shipmentId: number,
  cartonNumber: number,
  printer: string,
  options?: { allowPending?: boolean },
): Promise<{ printed: boolean; lineCount: number; labelCount?: number }> {
  return apiFetch<{ printed: boolean; lineCount: number; labelCount?: number }>(
    `/api/prepack/shipments/${shipmentId}/cartons/${cartonNumber}/print`,
    {
      method: "POST",
      body: { printer, ...(options?.allowPending ? { allowPending: true } : {}) },
    },
  );
}

export interface CartonQuantityTargetRow {
  key: string;
  sku: string;
  source: "serialized" | "unserialized";
  batchItemId: number | null;
  total: number;
  /** Quantity still on the batch and not on any shipment. Not part of the editable total. */
  mayStillJoin: number;
  packedByCarton: Record<string, number>;
  quantities: Record<string, number>;
}

export interface CartonQuantityAdvisory {
  sku: string;
  source: "serialized" | "unserialized";
  quantity: number;
}

export interface CartonQuantityTargets {
  shipmentId: number;
  cartonNumbers: number[];
  sealedCartonNumbers: number[];
  rows: CartonQuantityTargetRow[];
  advisories: CartonQuantityAdvisory[];
}

export async function fetchCartonQuantityTargets(shipmentId: number): Promise<CartonQuantityTargets> {
  return apiFetch<CartonQuantityTargets>(`/api/prepack/shipments/${shipmentId}/carton-quantity-targets`, {
    cache: "no-store",
  });
}

export async function saveCartonQuantityTargets(
  shipmentId: number,
  rows: CartonQuantityTargetRow[],
): Promise<{ saved: number }> {
  return apiFetch<{ saved: number }>(`/api/prepack/shipments/${shipmentId}/carton-quantity-targets`, {
    method: "POST",
    body: { rows },
  });
}

// ---------------------------------------------------------------------------
// Labor session — same shape as shipping; only the `page` value differs.
// ---------------------------------------------------------------------------

export interface LaborSession {
  id: number;
  started_at: string;
  device_count?: number;
  net_seconds?: number;
}

export interface LaborScan {
  device_serial: string;
  session_id: number;
}

export interface LaborSessionResult {
  idle_timeout_ms: number;
  heartbeat_interval_ms: number;
  session: LaborSession | null;
  scans: LaborScan[];
  was_stale: boolean;
  active_on_other_page: string | null;
}

export interface LaborStartResult {
  session: LaborSession | null;
  error?: string;
  page?: string;
}

export interface LaborEndResult {
  device_count?: number;
  net_seconds?: number;
  error?: string;
}

/** Operators with an open Pre-Pack carton labor session (for queue presence badges). */
export interface PrePackCartonOperator {
  userId: string;
  shipmentId: number;
  cartonNumber: number;
  email: string;
  fullName: string | null;
  avatarUrl: string | null;
}

/** Operators with an open transfer pack labor session (for queue presence badges). */
export interface PrePackTransferOperator {
  userId: string;
  transferOrderId: number;
  email: string;
  fullName: string | null;
  avatarUrl: string | null;
}

export const prePackActiveCartonOperatorsQueryKey = ["prepack", "labor", "active-carton-operators"] as const;
export const prePackActiveTransferOperatorsQueryKey = ["prepack", "labor", "active-transfer-operators"] as const;

export async function fetchPrePackActiveCartonOperators(): Promise<PrePackCartonOperator[]> {
  const data = await apiFetch<{ operators: PrePackCartonOperator[] }>("/api/labor/prepack/active-carton-operators", {
    cache: "no-store",
  });
  return data.operators;
}

export async function fetchPrePackActiveTransferOperators(): Promise<PrePackTransferOperator[]> {
  const data = await apiFetch<{ operators: PrePackTransferOperator[] }>(
    "/api/labor/prepack/active-transfer-operators",
    { cache: "no-store" },
  );
  return data.operators;
}

export async function fetchLaborSession(page?: string): Promise<LaborSessionResult | null> {
  const query =
    page !== undefined && page.trim() !== ""
      ? { page: page.trim(), domain: "shipping" }
      : ({} as Record<string, string>);
  const data = await apiFetch<LaborSessionResult | null>("/api/labor/active-session", {
    query,
  });
  return data;
}

export async function startLaborSession(page?: string): Promise<LaborStartResult> {
  const body = page !== undefined && page.trim() !== "" ? { page: page.trim(), domain: "shipping" } : { domain: "shipping" };
  return apiFetch<LaborStartResult>("/api/labor/start-session", {
    method: "POST",
    body,
  });
}

export async function endLaborSession(
  sessionId: number,
  idleTimeout: boolean,
): Promise<LaborEndResult | null> {
  return apiFetch<LaborEndResult | null>("/api/labor/end-session", {
    method: "POST",
    body: { sessionId, idleTimeout, domain: "shipping" },
  });
}

export async function sendLaborHeartbeat(sessionId: number): Promise<void> {
  await apiFetch("/api/labor/heartbeat", {
    method: "POST",
    body: { sessionId, domain: "shipping" },
  });
}

// ---------------------------------------------------------------------------
// Formatting helpers used by the queue list
// ---------------------------------------------------------------------------

export function formatShipDate(raw: string | null): string {
  if (!raw) return "-";
  const parsed = new Date(raw.includes("T") ? raw : `${raw}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return raw;
  if (parsed.getFullYear() < 1951) return "-";
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}
